import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import styles from "./user-edit.module.css";
import PageTitle from "../../components/screens/page-title";
import Button from "../../components/ui/button";
import Input from "../../components/ui/input";
import Dropdown from "../../components/ui/dropdown";
import Checkbox from "../../components/ui/checkbox";
import { editUserAdmin, getWorkers } from "../../services/apiService";
import { showSuccessToast, showErrorToast } from "../../utils/notifications";
import usePageTitle from "../../hooks/usePageTitle";

const UserEditPage = () => {
  usePageTitle('Editar Usuario');
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [userData, setUserData] = useState({
    username: "",
    nombre: "",
    activo: true,
    gruposTrabajo: [],
    rol: "",
    operarioId: null
  });
  
  const [operarios, setOperarios] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const roleOptions = [
    { value: "ROLE_USER", label: "Usuario" },
    { value: "ROLE_MANAGER", label: "Manager" },
    { value: "ROLE_ADMIN", label: "Administrador" },
    { value: "ROLE_OPERARIO", label: "Operario" },
  ];

  const grupoTrabajoOptions = [
    { value: "Grupo A", label: "Grupo A" },
    { value: "Grupo B", label: "Grupo B" },
  ];

  useEffect(() => {
    // Load operarios for dropdown
    const fetchOperarios = async () => {
      try {
        const operariosData = await getWorkers();
        const operariosList = operariosData.map(operario => ({
          value: operario.id,
          label: operario.nombre
        }));
        setOperarios(operariosList);
      } catch (error) {
        console.error("Error fetching operarios:", error);
      }
    };
    
    fetchOperarios();
    
    // Load user data from navigation state
    console.log("Location state:", location.state);
    if (location.state && location.state.user) {
      const user = location.state.user;
      console.log("User data from navigation:", user);
      // Convertir activo a booleano si viene como string
      let activoValue = true;
      if (user.activo !== undefined) {
        if (typeof user.activo === 'boolean') {
          activoValue = user.activo;
        } else if (user.activo === "Sí" || user.activo === "si") {
          activoValue = true;
        } else if (user.activo === "No" || user.activo === "no") {
          activoValue = false;
        }
      }
      // Manejar gruposTrabajo: puede venir como array o como string
      let gruposTrabajo = [];
      if (Array.isArray(user.gruposTrabajo)) {
        gruposTrabajo = user.gruposTrabajo;
      } else if (typeof user.gruposTrabajo === 'string' && user.gruposTrabajo) {
        // Si viene como string "GRUPO A, GRUPO B", convertir a array
        gruposTrabajo = user.gruposTrabajo.split(", ").map(g => g.trim()).filter(g => g);
      }
      
      setUserData({
        username: user.username || "",
        nombre: user.nombre || "",
        activo: activoValue,
        gruposTrabajo: gruposTrabajo,
        rol: user.rol || "",
        operarioId: user.operarioId || null
      });
    } else {
      console.log("No user data found in location state");
    }
  }, [userId, location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRoleSelect = (option) => {
    setUserData(prev => ({
      ...prev,
      rol: option.value,
      // Reset fields based on role
      gruposTrabajo: option.value === "ROLE_OPERARIO" ? [] : prev.gruposTrabajo,
      operarioId: option.value !== "ROLE_OPERARIO" ? null : prev.operarioId
    }));
  };

  const handleOperarioSelect = (option) => {
    setUserData(prev => ({
      ...prev,
      operarioId: option.value
    }));
  };

  const handleGrupoTrabajoSelect = (selectedOptions) => {
    setUserData(prev => ({
      ...prev,
      gruposTrabajo: selectedOptions.map(option => option.value)
    }));
  };

  const handleActivoChange = (isChecked) => {
    setUserData(prev => ({
      ...prev,
      activo: isChecked
    }));
  };

  const handleSave = async () => {
    if (!userData.username || !userData.nombre || !userData.rol) {
      showErrorToast("Todos los campos obligatorios deben estar completados");
      return;
    }

    setIsLoading(true);
    
    try {
      // Asegurar que activo sea booleano antes de enviar
      const dataToSend = {
        ...userData,
        activo: Boolean(userData.activo)
      };
      await editUserAdmin(userId, dataToSend);
      showSuccessToast("Usuario actualizado correctamente");
      
      // Navigate back after successful save
      setTimeout(() => {
        navigate("/user-management");
      }, 1500);
    } catch (error) {
      console.error("Error updating user:", error);
      showErrorToast("Error al actualizar el usuario");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/user-management");
  };

  return (
    <div className={styles.container}>
      <PageTitle iconName="userEdit" title="Editar Usuario" />
      
      <div className={styles.formSection}>
        <div className={styles.formGroup}>
          <Input
            label="Nombre de Usuario"
            name="username"
            value={userData.username}
            onChange={handleInputChange}
            placeholder="Nombre de usuario"
            style={{ width: "100%" }}
          />
        </div>
        
        <div className={styles.formGroup}>
          <Input
            label="Nombre Completo"
            name="nombre"
            value={userData.nombre}
            onChange={handleInputChange}
            placeholder="Nombre completo"
            style={{ width: "100%" }}
          />
        </div>
        
        <div className={styles.formGroup}>
          <Dropdown
            label="Rol"
            options={roleOptions}
            onSelect={handleRoleSelect}
            selected={roleOptions.find(option => option.value === userData.rol)}
            placeholder="Seleccionar rol"
          />
        </div>

        <div className={styles.formGroup}>
          <div className={styles.checkboxGroup}>
            <label className={styles.checkboxLabel}>Estado:</label>
            <Checkbox
              checked={userData.activo}
              onChange={handleActivoChange}
              label="Activo"
            />
          </div>
        </div>

        {/* Show grupos de trabajo for USER and MANAGER */}
        {(userData.rol === "ROLE_USER" || userData.rol === "ROLE_MANAGER") && (
          <div className={styles.formGroup}>
            <Dropdown
              label="Grupos de Trabajo"
              options={grupoTrabajoOptions}
              onSelect={handleGrupoTrabajoSelect}
              selected={grupoTrabajoOptions.filter(option => 
                userData.gruposTrabajo.includes(option.value)
              )}
              placeholder="Seleccionar grupos de trabajo"
              multiSelect={true}
            />
          </div>
        )}

        {/* Show operario selection for OPERARIO role */}
        {userData.rol === "ROLE_OPERARIO" && (
          <div className={styles.formGroup}>
            <Dropdown
              label="Operario Vinculado"
              options={operarios}
              onSelect={handleOperarioSelect}
              selected={operarios.find(option => option.value === userData.operarioId)}
              placeholder="Seleccionar operario"
            />
          </div>
        )}
        
        <div className={styles.buttonGroup}>
          <Button 
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? "Guardando..." : "Guardar"}
          </Button>
          <Button 
            onClick={handleCancel}
            variant="secondary"
          >
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserEditPage;
